/**
 * SubscriptionExpiresListModal.tsx
 * 
 * Modal to display users whose subscription/profile validity has expired or will expire
 */

import React, { useEffect, useState } from 'react';
import { X, Search, Calendar, Eye, Send, UserX, User } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { getApiBaseUrl, getMediaUrl } from '@/config/api';

type ExpiredUser = {
  userId: string;
  firstName: string;
  lastName: string;
  mobileNo: string;
  email: string;
  ProfileValidity: string | null;
  photoUrl?: string;
  isActive: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const API = getApiBaseUrl();

export default function SubscriptionExpiresListModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const [users, setUsers] = useState<ExpiredUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ExpiredUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 3;

  useEffect(() => {
    if (open) {
      fetchExpiredUsers();
    }
  }, [open, selectedDate]);

  useEffect(() => {
    filterUsers();
    setCurrentPage(1); // Reset to first page when search changes
  }, [users, searchQuery]);

  async function fetchExpiredUsers() {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/subscription-expires?date=${selectedDate}`);
      const result = await response.json();

      if (result.success) {
        setUsers(result.data || []);
      } else {
        toast.error(result.message || 'Failed to fetch expired subscriptions');
      }
    } catch (error) {
      console.error('Error fetching expired subscriptions:', error);
      toast.error('Error loading expired subscriptions');
    } finally {
      setLoading(false);
    }
  }

  function filterUsers() {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.userId.toLowerCase().includes(query) ||
        user.firstName.toLowerCase().includes(query) ||
        user.lastName.toLowerCase().includes(query) ||
        user.mobileNo.includes(query) ||
        user.email.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }

  async function handleInactivateUser(userId: string) {
    try {
      const confirmed = window.confirm('Are you sure you want to inactivate this user?');
      if (!confirmed) return;

      const response = await fetch(`${API}/api/admin/users/${userId}/inactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (result.success) {
        toast.success('User inactivated successfully');
        fetchExpiredUsers(); // Refresh list
      } else {
        toast.error(result.message || 'Failed to inactivate user');
      }
    } catch (error) {
      console.error('Error inactivating user:', error);
      toast.error('Error inactivating user');
    }
  }

  async function handleSendReminder(userId: string) {
    try {
      const response = await fetch(`${API}/api/admin/users/${userId}/send-reminder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'subscription_expiry' }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Reminder sent successfully');
      } else {
        toast.error(result.message || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Error sending reminder');
    }
  }

  function handleViewProfile(userId: string) {
    onClose();
    navigate(`/profile/${userId}`);
  }

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Subscription expires modal"
      className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8"
    >
      {/* Full-screen themed backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="fixed inset-0"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(2,6,23,0.75), rgba(7,16,26,0.75)), var(--dashboard-bg, linear-gradient(135deg,#8694a1 0%,#ecfeff 100%))',
          backdropFilter: 'blur(6px)',
        }}
      />

      {/* Modal container */}
      <div className="relative w-full max-w-7xl">
        <div className="bg-white rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/10 max-h-[85vh] flex flex-col">
          {/* Header */}
          <div
            className="px-6 py-5 flex items-center justify-between gap-4"
            style={{
              backgroundImage: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)',
              color: 'white',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Calendar className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-semibold leading-tight">Subscription Expires</h3>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

        {/* Search and Filter Bar */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by User ID, Name, Mobile No, or Email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            {/* Count Badge */}
            <div className="flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-lg font-semibold">
              {filteredUsers.length} User{filteredUsers.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
                <p className="text-gray-500">Loading expired subscriptions...</p>
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Expired Subscriptions</h3>
                <p className="text-gray-500">
                  {searchQuery ? 'No users match your search criteria' : 'All subscriptions are active'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedUsers.map((user) => (
                <div
                  key={user.userId}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    {/* User Photo */}
                    <div className="flex-shrink-0">
                      {user.photoUrl ? (
                        <img
                          src={getMediaUrl(user.photoUrl)}
                          alt={`${user.firstName} ${user.lastName}`}
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/64?text=No+Photo';
                          }}
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-xl">
                          {user.firstName.charAt(0)}
                          {user.lastName.charAt(0)}
                        </div>
                      )}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {user.firstName} {user.lastName}
                        </h3>
                        {!user.isActive && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-mono">{user.userId}</span>
                        </div>
                        <div className="truncate">{user.mobileNo}</div>
                        <div className="truncate">{user.email}</div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-red-500" />
                          <span className="text-red-600 font-semibold">
                            {user.ProfileValidity
                              ? new Date(user.ProfileValidity).toLocaleDateString()
                              : 'Not Set'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex-shrink-0 flex flex-col md:flex-row gap-2">
                      <button
                        onClick={() => handleViewProfile(user.userId)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <button
                        onClick={() => handleSendReminder(user.userId)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                      >
                        <Send className="w-4 h-4" />
                        Send
                      </button>
                      {user.isActive && (
                        <button
                          onClick={() => handleInactivateUser(user.userId)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 whitespace-nowrap"
                        >
                          <UserX className="w-4 h-4" />
                          InActive
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && filteredUsers.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg transition-colors ${
                        page === currentPage
                          ? 'bg-orange-600 text-white'
                          : 'bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
